import { Provider } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

type InvoiceResolutionProjection =
  Dashboardv99990101Api.PostInvoiceReminderVariableResolution.Body['invoice']
type CustomerResolutionProjection =
  Dashboardv99990101Api.PostInvoiceReminderVariableResolution.Body['customer']

import {
  Text,
  GreyGrey90,
  Primary14SemiBold
} from '@sequencehq/core-components'

import { ManualTaskLayout } from 'Automations/components'
import { ViewInvoiceButton } from 'Automations/components'
import { useManualTaskProcessorToolkit } from 'Automations/nodeDefinitions/hooks/useManualTaskProcessorToolkit'
import { TaskInput, TaskOutcome } from '../../types'
import { EmailPreview } from './EmailPreview'
import { EmailReviewForm } from './EmailReviewForm'
import { SendReminderButton } from './SendReminderButton'
import {
  bccAtom,
  bodyAtom,
  ccAtom,
  invoiceCustomerLegalNameAtom,
  customerProjectionAtom,
  invoiceIdAtom,
  invoiceProjectionAtom,
  invoiceNumberAtom,
  invoiceStatusAtom,
  showValidationErrorsAtom,
  subjectAtom,
  toAtom
} from './emailReview.atoms'

export const EmailReviewContent = ({ taskId }: { taskId: string }) => {
  const { manualTask, isPending, handleClose, completeTask } =
    useManualTaskProcessorToolkit<TaskInput>({
      taskId
    })

  if (isPending) {
    return <ManualTaskLayout.Loading />
  }

  if (!manualTask?.input) {
    return <ManualTaskLayout.Error />
  }

  return (
    <Provider>
      <EmailReviewTask
        taskId={taskId}
        input={manualTask.input}
        handleClose={handleClose}
        completeTask={completeTask}
      />
    </Provider>
  )
}

const EmailReviewTask = ({
  taskId,
  input,
  handleClose,
  completeTask
}: {
  taskId: string
  input: TaskInput
  handleClose: () => void
  completeTask: (outcome: TaskOutcome) => Promise<boolean>
}) => {
  useHydrateAtoms([
    [toAtom, input.to],
    [ccAtom, input.cc],
    [bccAtom, input.bcc],
    [subjectAtom, input.subject],
    [bodyAtom, input.text],
    [showValidationErrorsAtom, false],
    [invoiceIdAtom, input.invoice.id],
    [invoiceProjectionAtom, input.invoice as InvoiceResolutionProjection],
    [
      customerProjectionAtom,
      input.customer as unknown as CustomerResolutionProjection
    ],
    [invoiceNumberAtom, input.invoiceNumber],
    [invoiceStatusAtom, input.invoice.status],
    [invoiceCustomerLegalNameAtom, input.invoice.customerLegalCompanyName]
  ])

  const actions = (
    <ManualTaskLayout.Actions>
      <ViewInvoiceButton
        invoiceId={input.invoice.id}
        data-testid="watchtower.reviewEmail.viewInvoice"
      />
      <SendReminderButton completeTask={completeTask} />
    </ManualTaskLayout.Actions>
  )

  return (
    <ManualTaskLayout
      data-testid="watchtower.reviewEmail.manualTask"
      actions={actions}
      header={
        <ManualTaskLayout.Header
          taskId={taskId}
          title={
            <Text {...Primary14SemiBold} color={GreyGrey90}>
              Review and send email
            </Text>
          }
          onClose={handleClose}
        />
      }
      content={
        <ManualTaskLayout.Content
          templateColumns="1fr 1fr"
          subject={<EmailReviewForm />}
          review={<EmailPreview />}
        />
      }
    />
  )
}
